/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useRef, useEffect, useCallback, memo, useMemo } from "react";
import ImageComponent from "@/components/ui/image";
import { UniversalFoodIndicator } from "@/components/common/UniversalFoodIndicators";
import UniversalButton from "@/components/common/UniversalButton";
import Counter from "@/features/cake-customization/Counter";
import { Product } from "@/features/cake-customization/interfaces";
import { AiOutlineClose } from "react-icons/ai";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { getProductListing } from "@/services/api/product/listing";
import { useInView } from "react-intersection-observer";
import { useSearchParams, useRouter } from "next/navigation";
import Link from "next/link";
import { getAllDietaryCategories } from "@/services/api/product/details";
import { useCheckout } from "@/contexts/checkout";
import { getProductDietaryCategories as getProductDietaryCategoriesUtil, getAvailableFlavors, getInitialFormData, calculatePrice } from "@/utils/product/detail";
// import { DietaryCategory } from "@/types/product";
import ProductForm from "@/components/product/details/ProductDetailView/sections/ProductForm";
import CustomizationModal from "@/components/product/details/ProductDetailView/sections/CustomizationModal";
import { sendGAEvent } from "@next/third-parties/google";
import CustomisationOptions from "@/components/common/CustomisationOptions";
import { getCartItemUniqueId } from "@/utils/cart";
interface ProductCardProps {
  originalProduct: any;
  allDietaryCategories: any[];
  vendor_offline?: boolean;
  vendorSlug?: string;
}
interface MerchantLayoutProps {
  menuSections: MenuSection[];
  onSectionRef?: (sectionName: string, ref: React.RefObject<HTMLDivElement>) => void;
  vendorId: string;
  initialProducts?: any[];
  vendorSlug?: string;
  vendor_offline?: boolean;
}
interface MenuSection {
  id: number;
  name: string;
  product_count?: number;
}
const MerchantLayoutComponent: React.FC<MerchantLayoutProps> = memo(t0 => {
  const $ = _c(18);
  const {
    menuSections,
    onSectionRef,
    vendorId,
    initialProducts,
    vendorSlug,
    vendor_offline
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Map();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const sectionRefs = useRef(t1);
  let t2;
  if ($[1] !== onSectionRef) {
    t2 = (sectionName, ref) => {
      sectionRefs.current.set(sectionName, ref);
      onSectionRef?.(sectionName, ref);
    };
    $[1] = onSectionRef;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleSectionRef = t2;
  const menuSectionsMemo = menuSections;
  let t3;
  if ($[3] !== handleSectionRef || $[4] !== initialProducts || $[5] !== menuSectionsMemo || $[6] !== vendorId || $[7] !== vendorSlug || $[8] !== vendor_offline) {
    let t4;
    if ($[10] !== handleSectionRef || $[11] !== initialProducts || $[12] !== vendorId || $[13] !== vendorSlug || $[14] !== vendor_offline) {
      t4 = (section, index) => <MenuSectionComponent key={section?.id} section={section} sectionIndex={index} onSectionRef={handleSectionRef} vendorId={vendorId} initialProducts={initialProducts} vendorSlug={vendorSlug} vendor_offline={vendor_offline} />;
      $[10] = handleSectionRef;
      $[11] = initialProducts;
      $[12] = vendorId;
      $[13] = vendorSlug;
      $[14] = vendor_offline;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    t3 = menuSectionsMemo.map(t4);
    $[3] = handleSectionRef;
    $[4] = initialProducts;
    $[5] = menuSectionsMemo;
    $[6] = vendorId;
    $[7] = vendorSlug;
    $[8] = vendor_offline;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[16] !== t3) {
    t4 = <div className="space-y-12 max-w-[1440px] mx-auto w-full">{t3}</div>;
    $[16] = t3;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  return t4;
});
const MerchantLayout = MerchantLayoutComponent;
interface MenuSectionComponentProps {
  section?: MenuSection;
  sectionIndex?: number;
  onSectionRef?: (sectionName: string, ref: React.RefObject<HTMLDivElement>) => void;
  searchQuery?: string;
  vendorId: string;
  vendor_offline?: boolean;
  initialProducts?: any[];
  vendorSlug?: string;
}
const MenuSectionComponentInternal: React.FC<MenuSectionComponentProps> = memo(({
  section,
  sectionIndex,
  onSectionRef,
  searchQuery,
  vendorId,
  vendor_offline,
  initialProducts,
  vendorSlug
}) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const searchParams = useSearchParams();
  const selectedSort = useMemo(() => searchParams.get("sort_by") || "", [searchParams]);

  // Use intersection observer for infinite scroll
  const {
    ref: handleRef,
    inView
  } = useInView({
    threshold: 0.1,
    triggerOnce: false,
    rootMargin: "100px" // Start loading before the section is fully visible
  });

  // Combine refs
  const combinedRef = useCallback((node: HTMLDivElement | null) => {
    // Set the ref for intersection observer
    if (typeof handleRef === "function") {
      handleRef(node);
    }
    // Set the ref for section navigation
    if (sectionRef.current !== node) {
      (sectionRef as any).current = node;
    }
    // Register the section ref with parent component
    if (node && onSectionRef) {
      onSectionRef(section?.name || "", sectionRef);
    }
  }, [handleRef, onSectionRef, section?.name]);
  const modifiedSearchParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set("vendor_id", vendorId.toString());
    if (selectedSort) {
      params.set("sort_by", selectedSort);
    }
    if (searchQuery) {
      params.set("name__icontains", searchQuery);
    }
    return params;
  }, [vendorId, selectedSort, searchQuery]);
  const sectionInitialProducts = useMemo(() => {
    if (selectedSort || searchQuery || !initialProducts || initialProducts.length === 0) {
      return [];
    }
    if (!section?.id) {
      return initialProducts;
    }
    return initialProducts.filter((p: any) => p.menu?.id === section.id);
  }, [selectedSort, searchQuery, initialProducts, section?.id]);
  const {
    data: productsData,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery({
    queryKey: ["products", vendorId, section?.id, selectedSort, searchQuery],
    queryFn: ({
      pageParam = 1
    }) => {
      return getProductListing({
        searchParams: modifiedSearchParams,
        hasThemeCake: true,
        page: pageParam,
        menuId: section?.id.toString() || ""
      });
    },
    getNextPageParam: lastPage => {
      // Extract page number from the next URL if it exists
      if (lastPage?.next) {
        try {
          const url = new URL(lastPage.next);
          const pageParam_0 = url.searchParams.get("page");
          return pageParam_0 ? parseInt(pageParam_0) : undefined;
        } catch {
          return typeof lastPage.next === "number" ? lastPage.next : undefined;
        }
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled: true,
    // Always fetch for all loaded sections
    staleTime: 60 * 1000
  });
  const noResultsView = useCallback(() => {
    return <div className="min-h-[400px] flex flex-col items-center justify-center">
          <div className="text-center">
            <div className="text-2xl font-outfit text-[#3E3F41] mb-2">
              No results found
            </div>
            <div className="text-lg font-outfit text-[#757575] mb-4">
              We couldn&apos;t find any products matching &quot;{searchQuery}
              &quot;
            </div>
            <div className="text-sm font-outfit text-[#757575]">
              Try adjusting your search terms or browse our menu categories
            </div>
          </div>
        </div>;
  }, [searchQuery]);
  if (!isLoading && !productsData?.pages?.[0]?.results?.length && searchQuery) {
    return noResultsView();
  }

  // Fetch next page when intersection observer triggers
  return <ProductList isLoading={isLoading} isError={isError} inView={inView} hasNextPage={hasNextPage} isFetchingNextPage={isFetchingNextPage} fetchNextPage={fetchNextPage} productsData={productsData} section={section} sectionIndex={sectionIndex} combinedRef={combinedRef} vendor_offline={vendor_offline} vendorSlug={vendorSlug} ssrProducts={sectionInitialProducts} />;
});
const ProductList: React.FC<{
  isLoading: boolean;
  isError: boolean;
  inView: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  productsData: any;
  section: any;
  sectionIndex?: number;
  vendor_offline?: boolean;
  vendorSlug?: string;
  combinedRef: React.RefObject<HTMLDivElement> | ((node: HTMLDivElement | null) => void);
  ssrProducts?: any[];
}> = memo(t0 => {
  const $ = _c(49);
  const {
    isLoading,
    isError,
    inView,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    productsData,
    section,
    combinedRef,
    vendor_offline,
    vendorSlug,
    ssrProducts
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryKey: ["dietary-categories"],
      queryFn: _temp2
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    data: allDietaryCategories
  } = useQuery(t1);
  let t2;
  let t3;
  if ($[1] !== fetchNextPage || $[2] !== hasNextPage || $[3] !== inView || $[4] !== isFetchingNextPage) {
    t2 = () => {
      if (inView && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    };
    t3 = [inView, hasNextPage, isFetchingNextPage, fetchNextPage];
    $[1] = fetchNextPage;
    $[2] = hasNextPage;
    $[3] = inView;
    $[4] = isFetchingNextPage;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  const hasAuthoritativeData = Boolean(productsData && productsData.pages && productsData.pages.length > 0);
  let t4;
  if ($[7] !== productsData?.pages) {
    const products = productsData?.pages?.flatMap(_temp3) || [];
    const seenIds = new Set();
    t4 = products.filter(product => {
      if (!product || seenIds.has(product.id)) {
        return false;
      }
      seenIds.add(product.id);
      return true;
    });
    $[7] = productsData?.pages;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const authoritativeProducts = t4;
  let t5;
  bb0: {
    if (hasAuthoritativeData) {
      t5 = authoritativeProducts;
      break bb0;
    }
    let t6;
    if ($[9] !== ssrProducts) {
      t6 = ssrProducts || [];
      $[9] = ssrProducts;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    t5 = t6;
  }
  const displayedProducts = t5;
  let t6;
  if ($[11] !== section?.id) {
    t6 = () => <div className="grid grid-cols-1 md:grid-cols-2 md:gap-8">{Array.from({
        length: 4
      }).map((_, index) => <ProductCardSkeleton key={`skeleton-${section?.id}-${index}`} />)}</div>;
    $[11] = section?.id;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  section?.id;
  let t7;
  if ($[13] !== t6) {
    t7 = t6();
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const loadingView = t7;
  section?.name;
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="text-lg font-outfit text-[#3E3F41] mb-2">Error loading products</div>;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const t9 = section?.name;
  let t10;
  if ($[16] !== t9) {
    t10 = <div className="min-h-[200px] flex items-center justify-center"><div className="text-center">{t8}<div className="text-sm font-outfit text-[#757575]">Unable to load products for {t9}</div></div></div>;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const errorView = t10;
  let t11;
  if ($[18] !== allDietaryCategories || $[19] !== displayedProducts || $[20] !== section?.id || $[21] !== vendorSlug || $[22] !== vendor_offline) {
    t11 = () => <div className="grid grid-cols-1 md:grid-cols-2 md:gap-8">{displayedProducts.map((product_0, index_0) => <React.Fragment key={`${section?.id}-${product_0.id || index_0}`}><ProductCard originalProduct={product_0} allDietaryCategories={allDietaryCategories || []} vendor_offline={vendor_offline} vendorSlug={vendorSlug} /></React.Fragment>)}</div>;
    $[18] = allDietaryCategories;
    $[19] = displayedProducts;
    $[20] = section?.id;
    $[21] = vendorSlug;
    $[22] = vendor_offline;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  section?.id;
  let t12;
  if ($[24] !== t11) {
    t12 = t11();
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const productsGrid = t12;
  let t13;
  if ($[26] !== isFetchingNextPage) {
    t13 = isFetchingNextPage && <div className="flex justify-center py-4"><div className="text-sm font-outfit text-[#3E3F41]">Loading more products...</div></div>;
    $[26] = isFetchingNextPage;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const loadingIndicator = t13;
  let t14;
  if ($[28] !== hasNextPage) {
    t14 = hasNextPage && <div className="h-4" />;
    $[28] = hasNextPage;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const intersectionTarget = t14;
  if (hasAuthoritativeData && displayedProducts.length === 0) {
    return null;
  }
  const t15 = section?.id ? `menu-${section.id}` : undefined;
  let t16;
  if ($[30] !== displayedProducts.length || $[31] !== section?.name || $[32] !== section?.product_count) {
    t16 = section?.name && <h2 className="lg:text-2xl text-base font-semibold font-outfit text-[#3E3F41] text-left">{section?.name.toUpperCase()} ({displayedProducts.length > 0 ? displayedProducts.length : section?.product_count ?? 0})</h2>;
    $[30] = displayedProducts.length;
    $[31] = section?.name;
    $[32] = section?.product_count;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== displayedProducts.length || $[35] !== errorView || $[36] !== hasAuthoritativeData || $[37] !== intersectionTarget || $[38] !== isError || $[39] !== isLoading || $[40] !== loadingIndicator || $[41] !== loadingView || $[42] !== productsGrid) {
    t17 = isError && displayedProducts.length === 0 ? errorView : (isLoading || !hasAuthoritativeData) && displayedProducts.length === 0 ? loadingView : <>{productsGrid}{loadingIndicator}{intersectionTarget}</>;
    $[34] = displayedProducts.length;
    $[35] = errorView;
    $[36] = hasAuthoritativeData;
    $[37] = intersectionTarget;
    $[38] = isError;
    $[39] = isLoading;
    $[40] = loadingIndicator;
    $[41] = loadingView;
    $[42] = productsGrid;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== combinedRef || $[45] !== t15 || $[46] !== t16 || $[47] !== t17) {
    t18 = <div id={t15} ref={combinedRef} className="md:space-y-6 space-y-2 scroll-mt-20 md:scroll-mt-24">{t16}{t17}</div>;
    $[44] = combinedRef;
    $[45] = t15;
    $[46] = t16;
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  return t18;
});
const ProductCardSkeleton: React.FC = memo(() => {
  const $ = _c(8);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="lg:w-56 lg:h-56 rounded-lg w-32 h-32 bg-gray-200" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="w-3/4 h-6 bg-gray-200 rounded mt-2" />;
    t2 = <div className="w-16 h-6 bg-gray-200 rounded mt-2" />;
    t3 = <div className="w-full h-4 bg-gray-200 rounded mt-2" />;
    t4 = <div className="w-2/3 h-4 bg-gray-200 rounded mt-1" />;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="flex items-center space-x-2"><div className="w-4 h-4 bg-gray-200 rounded-full" /><div className="w-12 h-3 bg-gray-200 rounded" /></div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="flex lg:hidden"><div className="w-16 h-4 bg-gray-200 rounded" /></div>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="bg-white rounded-lg w-full flex h-[232px] items-start lg:py-0 py-2 animate-pulse">{t0}<div className="w-3/4 lg:pl-4 pl-3">{t1}{t2}{t3}{t4}<div className="flex items-center gap-2 mt-4 justify-between">{t5}{t6}<div className="lg:flex hidden"><div className="w-24 h-8 bg-gray-200 rounded-full" /></div></div></div></div>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  return t7;
});
const ProductCard: React.FC<ProductCardProps> = memo(({
  originalProduct,
  allDietaryCategories,
  vendor_offline,
  vendorSlug
}) => {
  const productData = originalProduct;
  const productVendorSlug = (productData as any).vendor_details?.slug || vendorSlug || "";
  const productHref = productData?.slug && productVendorSlug ? `/${productVendorSlug}/product/${productData.slug}` : `/product/${productData?.slug || ""}`;

  // Memoize the product transformation to avoid redundant work
  const product: Product = useMemo(() => ({
    image: productData.product_urls?.[0] || "",
    title: productData.name,
    subtitle: productData.product_subtype_details?.name || "",
    price: parseFloat(productData.base_price_amount.toString()),
    rating: productData.rating || 0,
    totalUsers: productData.total_users || 0,
    dietaryCategories: getProductDietaryCategoriesUtil({
      allDietaryCategories: allDietaryCategories || [],
      productDietaryCategoryIds: productData.dietary_categories || []
    }),
    vendorName: productData.vendor_details?.business_name || "Unknown",
    type: typeof productData.dietary_type === "string" ? productData.dietary_type?.toLowerCase() : "",
    overlayText: productData.overlay_text,
    overlaySubText: productData.overlay_sub_text,
    pillText: productData.is_best_product ? "Best Seller" : "",
    description: (productData.description || "").replace(/<[^>]*>?/gm, " ").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/\s+/g, " ").trim(),
    custom: (productData.customization_info?.length ?? 0) > 0
  }), [productData, allDietaryCategories]);
  const router = useRouter();
  const availableFlavors = getAvailableFlavors(productData as any);
  const isInStock = (productData as any)?.in_stock;
  const renderOpaqueTitle = () => {
    if (vendor_offline) return "UNAVAILABLE";
    if (!isInStock) return "SOLD OUT";
    return null;
  };
  const defaultFormData = useMemo(() => getInitialFormData(productData as any), [productData]);
  const [formData, setFormData] = useState<any>(defaultFormData);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isCustomizationModalOpen, setIsCustomizationModalOpen] = useState(false);
  const [isPlusChoiceOpen, setIsPlusChoiceOpen] = useState(false);
  const [isMinusChoiceOpen, setIsMinusChoiceOpen] = useState(false);
  // Local UI state for modals only; quantity display derives from cart state

  // Disable background scroll when modals are open
  useEffect(() => {
    if (isFormVisible || isCustomizationModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    // Cleanup function to re-enable scroll when component unmounts
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isFormVisible, isCustomizationModalOpen]);
  useEffect(() => {
    if (!isCustomizationModalOpen) return;
    const scrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      window.scrollTo(0, scrollY);
    };
  }, [isCustomizationModalOpen]);
  const {
    products: cartItems,
    addProduct,
    updateProductQuantity,
    setIsCartOpen
  } = useCheckout();

  // Utility function to check customization options and determine modal behavior
  const getCustomizationConfig = useCallback(() => {
    const hasEgglessOption = (productData as any)?.has_eggless_option;
    const hasMultipleWeightVariants = (productData as any)?.weight_variants?.length > 1;
    const isThemeCakeWithFlavors = (productData as any)?.is_theme_cake && availableFlavors.length > 1;
    const showCustomMessage = (productData as any)?.metadata?.show_custom_message;
    const hasCustomizationOptions = (productData as any)?.customization_info?.length > 0;
    const shouldSkipFirstModal = !hasEgglessOption && !isThemeCakeWithFlavors && !showCustomMessage;
    const hasAnyCustomization = hasEgglessOption || hasMultipleWeightVariants || isThemeCakeWithFlavors || showCustomMessage || hasCustomizationOptions;
    return {
      hasEgglessOption,
      hasMultipleWeightVariants,
      isThemeCakeWithFlavors,
      showCustomMessage,
      hasCustomizationOptions,
      shouldSkipFirstModal,
      hasAnyCustomization
    };
  }, [productData, availableFlavors]);
  const incrementCount = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (formData.count < 10) {
      setFormData((prev: any) => ({
        ...prev,
        count: prev.count + 1
      }));
    }
  };
  const decrementCount = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (formData.count > 1) {
      setFormData((prev: any) => ({
        ...prev,
        count: prev.count - 1
      }));
    }
  };
  const toggleEggless = () => {
    setFormData((prev: any) => ({
      ...prev,
      isEggless: !prev.isEggless
    }));
  };
  const handleMessageChange = (message: string) => {
    setFormData((prev: any) => ({
      ...prev,
      selectedMessage: message
    }));
  };
  const handleCustomizedNameChange = (name: string) => {
    setFormData((prev: any) => ({
      ...prev,
      customizedName: name
    }));
  };
  const handleFlavorChange = (flavor: {
    name: string;
    id: string;
  }) => {
    setFormData((prev: any) => {
      const updatedFormData = {
        ...prev,
        selectedFlavor: flavor
      };

      // For theme cakes, update the selected variant to match the new flavor
      if ((productData as any)?.is_theme_cake) {
        // First try to find a variant with the same weight and new flavor
        let matchingVariant = (productData as any).weight_variants.find((variant: any) => variant.weight === prev.selectedVariant?.weight && variant.flavour?.id === flavor.id);

        // If no variant with same weight exists for this flavor, select the first available variant for this flavor
        if (!matchingVariant) {
          matchingVariant = (productData as any).weight_variants.find((variant: any) => variant.flavour?.id === flavor.id);
        }
        if (matchingVariant) {
          updatedFormData.selectedVariant = matchingVariant;
        }
      }
      return updatedFormData;
    });
  };

  // Aggregate total quantity for this product across all customizations
  const totalProductQuantity = useMemo(() => {
    if (!productData?.id) return 0;
    return (cartItems || []).filter((ci: any) => ci.product_id === (productData as any).id).reduce((sum: number, ci: any) => sum + (ci.count || 0), 0);
  }, [cartItems, productData]);

  // Calculate total price for this product across all cart items
  const totalProductPrice = useMemo(() => {
    if (!productData?.id) return 0;
    const productCartItems = (cartItems || []).filter((ci: any) => ci.product_id === (productData as any).id);
    return productCartItems.reduce((sum: number, ci: any) => {
      // Since final_price_calculated now represents the total price for the item,
      // we just add it directly instead of multiplying by count
      return sum + parseFloat(ci.final_price_calculated || "0");
    }, 0);
  }, [cartItems, productData]);
  const handleAddToCart = async (e?: React.MouseEvent<HTMLButtonElement>, item?: any) => {
    if (e) {
      e?.stopPropagation();
      e?.preventDefault();
    }
    if (!item || !(item as any).product_id) {
      return;
    }
    await addProduct({
      product: {
        ...item,
        final_price_calculated: calculatePrice(item, productData),
        vendor_details: (productData as any).vendor_details,
        gst_rate: (productData as any).gst_rate,
        slug: (productData as any).slug,
        uniqueId: getCartItemUniqueId(item),
        product_subtype_name: (productData as any).product_subtype_details?.name,
        product_type_name: (productData as any).product_type_details?.name,
        availableWeightVariantsCount: (productData as any)?.weight_variants?.length || 0
      },
      isBuyNowAction: false
    });
  };
  const handlePlusClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (!productData || !(productData as any).id) {
      return;
    }
    const config = getCustomizationConfig();
    if (config.hasAnyCustomization) {
      setIsPlusChoiceOpen(true);
      return;
    }

    // If no customization, find the existing item in cart if any, or use formData
    const existingItem = cartItems.filter((ci: any) => ci.product_id === (productData as any).id).sort((a: any, b: any) => (b.timeStamp || 0) - (a.timeStamp || 0))[0];
    await updateProductQuantity({
      product: existingItem || formData,
      quantity: 1,
      action: "add"
    });
    setIsCartOpen(true);
  };
  const handleMinusClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (!productData || !(productData as any).id) {
      return;
    }
    const productCartItems = cartItems.filter((ci: any) => ci.product_id === (productData as any).id);
    if (productCartItems.length > 1) {
      setIsMinusChoiceOpen(true);
      return;
    }
    if (productCartItems.length === 1) {
      await updateProductQuantity({
        product: productCartItems[0],
        quantity: 1,
        action: "remove"
      });
    }
  };
  const handleCustomizationModalClose = () => {
    setIsCustomizationModalOpen(false);
    setIsFormVisible(false);
    setFormData(defaultFormData);
  };
  const handleFormModalClose = () => {
    setIsFormVisible(false);
    setIsCustomizationModalOpen(false);
    setFormData(defaultFormData);
  };
  const handleCustomizationModalAddToCart = async (data: any) => {
    await handleAddToCart(undefined, data);
    handleCustomizationModalClose();
  };
  const handleUsePreviousCustomizations = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e?.stopPropagation();
      e?.preventDefault();
    }
    setIsPlusChoiceOpen(false);
    const allInstancesOfProduct = cartItems.filter((ci: any) => ci.product_id === (productData as any).id).sort((a: any, b: any) => b.timeStamp - a.timeStamp);
    await updateProductQuantity({
      product: allInstancesOfProduct[0],
      quantity: 1,
      action: "add"
    });
    setIsCartOpen(true);
  };
  const handleAddNewCustomizations = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e?.stopPropagation();
      e?.preventDefault();
    }
    setIsPlusChoiceOpen(false);
    const config = getCustomizationConfig();
    if (config.shouldSkipFirstModal) {
      if (config.hasCustomizationOptions || config.hasMultipleWeightVariants) {
        // Go directly to customization modal (Step 2)
        setIsCustomizationModalOpen(true);
      } else {
        // Add directly to cart with default values
        await handleAddToCart(undefined, formData);
      }
    } else {
      // Show the first modal (has something to configure)
      setIsFormVisible(true);
    }
  };
  const handleModalAddToCart = async () => {
    // Check if product has customization options (including multiple weights now)
    const config = getCustomizationConfig();
    if (config.hasCustomizationOptions || config.hasMultipleWeightVariants) {
      // Close step 1 modal and show customization modal (step 2)
      setIsFormVisible(false);
      setIsCustomizationModalOpen(true);
    } else {
      // Add directly to cart
      await handleAddToCart(undefined, formData);
      setIsFormVisible(false);
    }
  };
  const renderAddToCartButton = useMemo(() => {
    if (totalProductQuantity <= 0) {
      return <UniversalButton classNames="border-2 border-[#037A6A] lg:text-base text-sm rounded-lg bg-white !text-[#037A6A] !tracking-tight px-6 md:py-2 py-1" text="ADD " onClick={handleAddNewCustomizations} />;
    }
    return <div className="flex flex-col items-center gap-2">
          <Counter count={totalProductQuantity} maxCount={100} minCount={0} classNames="!rounded-lg md:py-[6px] py-1 text-sm lg:text-base" onIncrement={(e: React.MouseEvent<HTMLButtonElement>) => {
        void handlePlusClick(e);
      }} onDecrement={(e: React.MouseEvent<HTMLButtonElement>) => {
        void handleMinusClick(e);
      }} />
        </div>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalProductQuantity, formData, handlePlusClick, handleMinusClick]);
  const renderModalHeader = () => <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Customize {product.title}</h3>
        <button onClick={() => handleFormModalClose()} className="text-gray-500 hover:text-gray-700">
          <AiOutlineClose size={24} />
        </button>
      </div>;
  const renderCustomizationOptionModalHeader = () => {
    return <div className="flex justify-between items-center gap-4 p-4 sm:p-6 border-b border-gray-200">
          <div className="flex-1 min-w-0 pr-2">
            <h3 className="text-lg font-semibold">Customize {product.title}</h3>
          </div>
          <button onClick={() => handleFormModalClose()} className="text-gray-500 hover:text-gray-700 flex-shrink-0 p-1">
            <AiOutlineClose size={24} />
          </button>
        </div>;
  };
  const handleCardClick = (e: React.MouseEvent) => {
    // If clicked on an <a> tag, button, or within a counter, don't trigger the card link
    if ((e.target as HTMLElement).closest("a") || (e.target as HTMLElement).closest("button") || (e.target as HTMLElement).closest(".counter-container")) {
      return;
    }
    sendGAEvent("event", "select_item", {
      currency: "INR",
      value: Number(productData?.base_price_amount),
      items: [{
        item_id: String(productData.product_id),
        item_name: productData.name,
        item_brand: productData.vendor_details?.business_name,
        item_category: productData.product_type_name,
        item_category2: productData.product_subtype_details?.name,
        price: Number(productData.base_price_amount),
        quantity: 1
      }]
    });
    router.push(`/${(productData as any).vendor_details?.slug}/product/${productData?.slug}`);
  };
  return <>
        <div className={`bg-white rounded-lg w-full md:flex items-start max-lg:py-3 mb-2 hidden cursor-pointer ${!isInStock ? "opacity-80 " : ""}`} onClick={handleCardClick}>
          {/* Image Section */}
          <Link href={productHref} className="md:aspect-square w-[140px] max-md:h-[120px] md:w-60 md:min-w-60 rounded-lg relative overflow-hidden block" onClick={() => {
        sendGAEvent("event", "select_item", {
          currency: "INR",
          value: Number(productData?.base_price_amount),
          items: [{
            item_id: String(productData.product_id || productData.id),
            item_name: productData.name,
            item_brand: productData.vendor_details?.business_name,
            item_category: productData.product_type_name,
            item_category2: productData.product_subtype_details?.name,
            price: Number(productData.base_price_amount),
            quantity: 1
          }]
        });
      }}>
            <ImageComponent src={product.image || "/placeholder.svg"} alt={product.title} objectFit="cover" width={240} height={240} className="transition-transform duration-300 hover:scale-105 object-cover" quality={100} sizes="(max-width: 768px) 140px, 240px" />
            {/* Unavailable overlay and label (image only) */}
            {/* TODO: Add unavailable overlay when implementing delivery logic */}
            {product.overlayText && <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent text-white p-4 font-serif hidden lg:block">
                <h3 className="text-lg font-semibold font-serif">
                  {product.overlayText}
                </h3>
                {product.overlaySubText && <p className="text-sm">{product.overlaySubText}</p>}
              </div>}
            {product.pillText && <div className="absolute top-2 right-2 bg-white px-[9.28px] rounded-full text-base font-medium text-[#079ea6] border-[1.16px] border-[#EAD520] shadow font-outfit italic hidden lg:block tracking-[-4%] leading-[100%] py-1">
                {product.pillText}
              </div>}
            <div className="font-dmsans text-[#767676] text-xs mt-4 absolute -bottom-10 w-full text-center ">
              {product.custom && "Customizable"}
            </div>

            {!isInStock && <>
                {/* Dark overlay on image only */}
                <div className="absolute inset-0 z-20 rounded-lg" />
                {/* Gradient bar with text */}
                <div className="absolute left-0 bottom-0 w-full bg-gradient-to-t from-[#252525]  h-[40px] px-3 py-1 flex flex-col items-start justify-end rounded-b-[12px] z-30">
                  <span className="font-serif md:text-[20px] text-sm font-normal text-white leading-[27px]">
                    {renderOpaqueTitle()}
                  </span>
                </div>
              </>}
          </Link>

          <div className={`w-3/4 lg:pl-4 pl-3 flex flex-col h-60 justify-between overflow-hidden`}>
            <Link href={productHref} className="block group" onClick={() => {
          sendGAEvent("event", "select_item", {
            currency: "INR",
            value: Number(productData?.base_price_amount),
            items: [{
              item_id: String(productData.product_id || productData.id),
              item_name: productData.name,
              item_brand: productData.vendor_details?.business_name,
              item_category: productData.product_type_name,
              item_category2: productData.product_subtype_details?.name,
              price: Number(productData.base_price_amount),
              quantity: 1
            }]
          });
        }}>
              <h3 className="lg:text-xl text-sm line-clamp-2 font-normal text-[#303030] group-hover:text-[#037A6A] transition-colors break-normal mb-2 font-serif leading-6 tracking-[-2%]" title={product.title}>
                {product.title}
              </h3>
              <div className="text-xl font-semibold text-[#2C2C2C] font-outfit my-2 tracking-[-2%] leading-[100%]">
                ₹{product.price}
              </div>
            </Link>
            <div className="text-base text-[#303030] font-outfit font-extralight line-clamp-3 leading-4 mb-2 overflow-hidden break-normal whitespace-normal w-full">
              {product.description}
            </div>
            <div className="flex items-start gap-2 justify-between">
              <div className=" flex flex-col justify-between h-full ">
                {product.type ? <UniversalFoodIndicator type={product.type} /> : <div className="h-6" />}
                <div>
                  {product.dietaryCategories?.slice(0, 2).map(({
                name
              }: {
                name: string;
              }, index: number, arr: any[]) => <span key={index} className="italic font-outfit text-[#5a5a5a] text-base font-medium">
                          {name}
                          {index < arr.length - 1 && " | "}
                        </span>)}
                  {product.dietaryCategories?.length > 2 && <span className="italic font-dmsans" title={product.dietaryCategories?.map(({
                name
              }: {
                name: string;
              }) => name).join(", ")}>
                      ...
                    </span>}
                </div>
              </div>
              {/* <ProductRating
               rating={product.rating}
               totalUsers={product.totalUsers}
               className="flex lg:hidden"
               /> */}
              <div className={`flex flex-col justify-end items-center ${!isInStock ? "pointer-events-none cursor-not-allowed opacity-50" : ""}`}>
                <div className="lg:flex hidden flex-col ">
                  {renderAddToCartButton}
                </div>
                {product.custom && <div className="font-dmsans text-gray-400 text-base tracking-[-2%] leading-[100%] pt-1">
                    {product.custom && "Customizable"}
                  </div>}
              </div>
            </div>
          </div>
        </div>

        {/* Customization Modal */}
        {isFormVisible && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 backdrop-blur-sm sm:flex sm:items-center sm:justify-center" onClick={handleFormModalClose}>
            <div onClick={e => e.stopPropagation()} className="bg-white p-6 max-w-md w-full max-h-[90vh] overflow-y-auto max-sm:fixed max-sm:bottom-0 max-sm:left-0 max-sm:right-0 max-sm:rounded-t-2xl max-sm:animate-slideUp sm:rounded-lg">
              {renderModalHeader()}

              <div className="space-y-4">
                <ProductForm product={productData as any} formData={formData} toggleEggless={toggleEggless} handleFlavorChange={handleFlavorChange} handleMessageChange={handleMessageChange} handleCustomizedNameChange={handleCustomizedNameChange} availableFlavors={availableFlavors}>
                  <React.Fragment></React.Fragment>
                </ProductForm>
              </div>

              <div className="flex items-center justify-between mt-6">
                {/* Quantity Selector */}
                <div className="flex items-center space-x-3">
                  <button onClick={decrementCount} className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100" disabled={formData.count <= 1}>
                    -
                  </button>
                  <span className="text-lg font-medium text-[#303030] min-w-[2rem] text-center">
                    {formData.count}
                  </span>
                  <button onClick={incrementCount} className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100" disabled={formData.count >= 10}>
                    +
                  </button>
                </div>

                {/* Add to Cart Button with Price */}
                <div className="flex items-center gap-2">
                  <button onClick={handleModalAddToCart} className="px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2 bg-[#037A6A] text-white hover:bg-[#049e8b]">
                    <span>Add to Cart</span>
                    <span className="border-l border-white h-4"></span>
                    <span>
                      ₹
                      {(() => {
                  const modalPrice = calculatePrice(formData, productData);
                  return parseFloat(modalPrice.toFixed(2).toString());
                })()}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>}

        {/* Plus choice modal */}
        {isPlusChoiceOpen && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-2">
                Add more of {product.title}?
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Choose to reuse your last customizations or add with new
                customizations.
              </p>
              <div className="flex flex-col gap-3">
                <UniversalButton text="Use last customizations" onClick={handleUsePreviousCustomizations} classNames="w-full bg-[#037A6A] text-white py-2 rounded-full" />
                <UniversalButton text="Add new customizations" onClick={handleAddNewCustomizations} classNames="w-full bg-white border border-gray-300 py-2 rounded-full !text-[#037A6A] hover:!text-gray-800" />
                <UniversalButton text="Cancel" onClick={() => setIsPlusChoiceOpen(false)} classNames="w-full bg-gray-200 text-gray-700 py-2 rounded-full" />
              </div>
            </div>
          </div>}

        {/* Minus choice modal: adjust quantities for each cart line */}
        {isMinusChoiceOpen && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-xl w-full max-h-[85vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-2">
                Your Customizations
              </h3>
              <p className="text-sm text-gray-600 mb-4">{product.title}</p>
              <div className="space-y-3">
                {cartItems.filter((ci: any) => ci.product_id === (productData as any).id).map((ci: any, idx: number) => {
            const uniqueId = ci.uniqueId as string;
            return <div key={`${uniqueId}-${idx}`} className="border rounded-lg p-3">
                        <div className="flex flex-col justify-center  items-start ">
                          <div className="text-sm text-gray-800">
                            <CustomisationOptions item={ci as any} />
                            {/* {noteText && (
                              <div className="text-xs text-gray-600">
                                {noteText}
                              </div>
                             )} */}
                          </div>
                          <div className="flex items-center justify-between w-full">
                            <div className="text-right min-w-">
                              <div className="text-lg font-semibold text-[#303030]">
                                ₹
                                {parseFloat(ci.final_price_calculated || "0").toFixed(2)}
                              </div>
                            </div>
                            <Counter count={ci.count || 0} maxCount={100} minCount={0} onIncrement={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    e.preventDefault();
                    updateProductQuantity({
                      product: ci,
                      quantity: 1,
                      action: "add"
                    });
                    setIsCartOpen(true);
                  }} onDecrement={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    e.preventDefault();
                    updateProductQuantity({
                      product: ci,
                      quantity: 1,
                      action: "remove"
                    });
                    setIsCartOpen(true);
                  }} />
                          </div>
                        </div>
                      </div>;
          })}
                {cartItems.filter((ci: any) => ci.product_id === (productData as any).id).length === 0 && <div className="text-sm text-gray-600">
                    No items to remove.
                  </div>}
              </div>
              {/* Total Summary */}
              <div className="border-t pt-4 mt-4 bg-gray-50 p-3 rounded-lg">
                <div className="flex justify-between items-center text-lg font-semibold text-[#303030]">
                  <span>Total</span>
                  <span className="text-[#037A6A]">
                    ₹
                    {parseFloat(Number(totalProductPrice).toFixed(2).toString())}
                  </span>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <UniversalButton text="Done" onClick={() => setIsMinusChoiceOpen(false)} classNames="flex-1 bg-[#037A6A] text-white py-2 rounded-full" ariaLabel="Confirm and close" />
                <UniversalButton text="Cancel" onClick={() => setIsMinusChoiceOpen(false)} classNames="flex-1 bg-gray-200 !text-black py-2 rounded-full" ariaLabel="Cancel choice update" />
              </div>
            </div>
          </div>}

        {/* Global Vendor Conflict Modal handles all conflicts - no local modal needed */}

        {isCustomizationModalOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 backdrop-blur-sm !m-0" onClick={handleCustomizationModalClose}>
            <div onClick={e => e.stopPropagation()} className="h-full w-full flex flex-col justify-end sm:justify-center items-center pt-14 sm:pt-0 sm:p-4">
              <CustomizationModal onClose={handleCustomizationModalClose} product={productData} formData={formData} onFormDataChange={setFormData} onAddToCart={(data: any) => handleCustomizationModalAddToCart(data)} renderHeaderContent={renderCustomizationOptionModalHeader} />
            </div>
          </div>}
        <div className={`bg-white w-full flex lg:hidden items-start space-x-3 pb-[27px] mb-3 pt-3 cursor-pointer ${!isInStock ? "opacity-80" : ""}`} onClick={handleCardClick}>
          <div className="w-[120px] relative h-[156px]">
            <Link href={productHref} className="w-full relative h-full overflow-hidden rounded-[5px] block" onClick={() => {
          sendGAEvent("event", "select_item", {
            currency: "INR",
            value: Number(productData?.base_price_amount),
            items: [{
              item_id: String(productData.product_id || productData.id),
              item_name: productData.name,
              item_brand: productData.vendor_details?.business_name,
              item_category: productData.product_type_name,
              item_category2: productData.product_subtype_details?.name,
              price: Number(productData.base_price_amount),
              quantity: 1
            }]
          });
        }}>
              <ImageComponent src={product.image || "/placeholder.svg"} alt={product.title} objectFit="cover" width={120} height={156} className="w-full h-full transition-transform duration-300 hover:scale-105 object-cover" sizes="120px" />
              {product.pillText && <div className="absolute top-2 right-2 bg-white px-[9.28px] rounded-full text-base font-medium text-[#079ea6] border-[1.16px] border-[#EAD520] shadow font-outfit italic hidden lg:block tracking-[-4%] leading-[100%] py-1">
                  {product.pillText}
                </div>}
            </Link>
            {isInStock && <>
                <div className="absolute bottom-0 z-20 overflow-hidden w-full flex justify-center translate-y-1/2 flex-col items-center ">
                  {renderAddToCartButton}
                </div>

                <div className="font-dmsans text-[#767676] text-xs  absolute -bottom-9 w-full text-center ">
                  {product.custom && "Customizable"}
                </div>
              </>}

            {!isInStock && <>
                {/* Dark overlay on image only */}
                <div className="absolute inset-0 z-20 rounded-lg " />
                {/* Gradient bar with text */}
                <div className="absolute left-0 bottom-0 w-full bg-gradient-to-t from-[#252525]   h-[40px] px-3 py-1 flex flex-col items-start justify-end rounded-b-[12px] z-30">
                  <span className="font-serif text-base leading-[0px] font-normal text-white py-2">
                    {renderOpaqueTitle()}
                  </span>
                </div>
              </>}
          </div>
          <div className="w-full flex flex-col h-[156px] justify-between overflow-hidden">
            <Link href={productHref} className="flex flex-col space-y-2 block" onClick={() => {
          sendGAEvent("event", "select_item", {
            currency: "INR",
            value: Number(productData?.base_price_amount),
            items: [{
              item_id: String(productData.product_id || productData.id),
              item_name: productData.name,
              item_brand: productData.vendor_details?.business_name,
              item_category: productData.product_type_name,
              item_category2: productData.product_subtype_details?.name,
              price: Number(productData.base_price_amount),
              quantity: 1
            }]
          });
        }}>
              <h3 className=" text-sm line-clamp-1 font-medium text-[#303030] break-normal  font-serif leading-5 tracking-[0]" title={product.title}>
                {product.title}
              </h3>
              <div className="text-base font-semibold text-[#2C2C2C] font-outfit tracking-[0%] mb-2 leading-[100%]">
                ₹{product.price}
              </div>
            </Link>
            <div className="flex flex-col ">
              <div className="text-xs text-[#303030] font-outfit font-extralight line-clamp-2 leading-4 overflow-hidden break-normal whitespace-normal w-full">
                {product.description}
              </div>
              {(product.type || (product.dietaryCategories?.length ?? 0) > 0) && <div className="flex items-start gap-2 justify-between pt-2">
                  <div className=" flex items-end justify-between h-full ">
                    {product.type ? <UniversalFoodIndicator type={product.type} /> : <div className="h-6" />}
                    <div className="h-full flex items-end truncate pl-1">
                      {product.dietaryCategories?.slice(0, 2).map(({
                  name
                }: {
                  name: string;
                }, index: number, arr: any[]) => <span key={index} className="italic font-outfit text-[#5a5a5a] text-xs font-medium">
                              {name}
                              {index < arr.length - 1 && " |  "}
                            </span>)}
                      {product.dietaryCategories?.length > 2 && <span className="italic font-outfit" title={product.dietaryCategories?.map(({
                  name
                }: {
                  name: string;
                }) => name).join(", ")}>
                          ...
                        </span>}
                    </div>
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </>;
});
MerchantLayout.displayName = "MerchantLayout";
MenuSectionComponentInternal.displayName = "MenuSectionComponentInternal";
export const MenuSectionComponent = MenuSectionComponentInternal;
MenuSectionComponent.displayName = "MenuSectionComponent";
ProductCardSkeleton.displayName = "ProductCardSkeleton";
ProductCard.displayName = "ProductCard";
export default MerchantLayout;
function _temp2() {
  return getAllDietaryCategories();
}
function _temp3(page) {
  return page.results;
}